import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getReturnTo,
  type LocationStateWithReturnTo,
} from '@sidecar/common/utils';
import {
  buildTaskPath,
  resolveTaskPaths,
  TASK_ROUTE_KEY,
  type TaskRouteKey,
  type TaskRouteParams,
} from './task-route.utils';

export type TaskLocationState = LocationStateWithReturnTo;

export type TaskNavigateOptions = {
  taskId?: string;
  subtaskId?: string;
  replace?: boolean;
  returnTo?: string;
};

export const useTaskRouteNavigation = () => {
  const navigate = useNavigate();
  const params = useParams<TaskRouteParams>();
  const location = useLocation();
  const taskPaths = useMemo(
    () => resolveTaskPaths(location.pathname),
    [location.pathname]
  );
  const returnTo = getReturnTo<TaskLocationState>(location.state);

  const navigateToRoute = useCallback(
    (routeKey: TaskRouteKey, options?: TaskNavigateOptions) => {
      if (!taskPaths) return;

      const target = buildTaskPath({
        path: taskPaths[routeKey],
        params,
        taskId: options?.taskId,
        subtaskId: options?.subtaskId,
      });

      if (!target) return;

      const nextReturnTo = options?.returnTo ?? returnTo;

      navigate(
        { pathname: target, search: location.search },
        {
          replace: options?.replace,
          ...(nextReturnTo && {
            state: { returnTo: nextReturnTo } satisfies TaskLocationState,
          }),
        }
      );
    },
    [navigate, params, taskPaths, returnTo, location.search]
  );

  const navigateToReturnOrRoot = useCallback(() => {
    if (returnTo) return navigate(returnTo);

    navigateToRoute(TASK_ROUTE_KEY.root);
  }, [navigate, navigateToRoute, returnTo]);

  return {
    params,
    pathname: location.pathname,
    taskPaths,
    returnTo,
    navigateToRoute,
    navigateToReturnOrRoot,
  };
};
